/* ── Sticky add to cart ──────────────────────────────────────────────────────
   Ported from Kap Industries 2.03. A collapsed card that expands into a variant
   picker, adds to cart against /cart/add.js and re-renders the cart drawer.

   Differences from the Kap original, forced by this theme:
   - Kap's upsell pills (custom.upsell_products metafield + page-wide
     .upsell-checkbox bundling) are dropped — Smokai has neither.
   - main-product and sm-reusable-product each render buy-buttons twice, from two
     different blocks, so ProductSubmitButton-<id> / product-form-<id> can appear
     more than once. Every lookup here handles the set, not the first hit.
   - sm-reusable-product names its price containers price-<id>-a / -b rather than
     price-<id>, so the price mirror resolves by prefix.
   ────────────────────────────────────────────────────────────────────────── */
(() => {
  'use strict';

  class StickyATC {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.section;
      this.expanded = false;
      this.hasVariantSelected = false;

      this.bar = el.querySelector('.sticky-atc__bar');
      this.panel = el.querySelector('.sticky-atc__panel');
      this.expandBtn = el.querySelector('.sticky-atc__expand-btn');
      this.closeBtn = el.querySelector('.sticky-atc__close-btn');
      this.barAddBtn = el.querySelector('[data-sticky-bar-add]');
      this.formWrap = el.querySelector('.sticky-atc__submit-wrap');
      this.panelForm = el.querySelector(`#sticky-product-form-${this.sectionId}`);
      this.variantInput = el.querySelector(`#sticky-atc-variant-id-${this.sectionId}`);
      this.optionBtns = el.querySelectorAll('.sticky-atc__option-btn');
      this.panelSubmitBtn = el.querySelector('.sticky-atc__submit');

      // Optional quantity picker. It is rendered twice — once in the collapsed
      // bar, once in the panel — so the value is held here and painted into both
      // rather than read off whichever input happens to be visible.
      this.qtyInputs = el.querySelectorAll('[data-sticky-qty-input]');
      this.qtyHidden = el.querySelector('[data-sticky-qty-hidden]');
      this.quantity = 1;

      const variantsEl = document.getElementById(`sticky-atc-variants-${this.sectionId}`);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.selectedOptions = {};
      this.moneyFormat = el.dataset.moneyFormat || '${{amount}}';
      this._ownPrice = false;
      this._wantsVisible = false;
      this._atcButtons = [];
      this._pastATC = false;

      // Scroll thresholds (vh) per breakpoint — 0 means no threshold
      this._thresholdDesktop = parseFloat(el.dataset.scrollDesktop) || 0;
      this._thresholdTablet  = parseFloat(el.dataset.scrollTablet)  || 0;
      this._thresholdMobile  = parseFloat(el.dataset.scrollMobile)  || 0;

      // Move to end of body after third-party widgets load (e.g. #shopify-chat)
      document.body.appendChild(this.el);
      setTimeout(() => document.body.appendChild(this.el), 1500);

      this._bindEvents();
      this._bindCamouflage();
      this._observeMainATC();
      this._observeCartDrawer();
      this._lowerChatWidget();
      this._bindScrollThreshold();
      this._observeMainPrice();

      // Auto-apply for single default variant or when variant gating is disabled;
      // otherwise show the selection prompt
      const isSingleDefault = this.variants.length === 1 && this.variants[0].title === 'Default Title';
      const requireVariant = el.dataset.requireVariant !== 'false';
      if (isSingleDefault || !requireVariant) {
        const firstAvailable = this.variants.find(v => v.available && !v.camouflageHidden) || this.variants[0];
        if (firstAvailable) this._applyVariant(firstAvailable);
      } else {
        this._showSelectPrompt();
      }
    }

    /* ── DOM lookups that must tolerate duplicate ids ───────────────────── */

    _mainSubmitButtons() {
      return document.querySelectorAll(`[id="ProductSubmitButton-${this.sectionId}"]`);
    }

    _mainVariantInputs() {
      return document.querySelectorAll(`[id="product-form-${this.sectionId}"] .product-variant-id`);
    }

    _bindEvents() {
      if (this.expandBtn) {
        this.expandBtn.addEventListener('click', () => this.togglePanel());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.collapsePanel());
      }

      this.optionBtns.forEach(btn => {
        btn.addEventListener('click', () => this._selectOption(btn));
      });

      this.el.querySelectorAll('[data-sticky-qty-step]').forEach(btn => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.stickyQtyStep === 'plus' ? 1 : -1;
          this._setQuantity(this.quantity + step);
        });
      });

      this.qtyInputs.forEach(input => {
        input.addEventListener('change', () => this._setQuantity(parseInt(input.value, 10)));
      });

      if (this.barAddBtn) {
        this.barAddBtn.addEventListener('click', () => {
          // Open the panel first when there are options to choose from, so the
          // shopper picks a variant before the item is added.
          if (this.expandBtn) {
            this.expandPanel();
          } else {
            this._submitCart();
          }
        });
      }

      if (this.panelForm) {
        this.panelForm.addEventListener('submit', e => {
          e.preventDefault();
          this._submitCart();
        });
      }

      document.addEventListener('click', e => {
        if (this.expanded && !this.el.contains(e.target)) {
          this.collapsePanel();
        }
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.expanded) this.collapsePanel();
      });

      // Sync when the shopper picks on the main product form.
      //
      // Delegated from document rather than bound to the picker: the theme swaps
      // the whole variant-selects element out when it re-renders the section, so a
      // listener attached to the original node stops firing after the first change.
      document.addEventListener('change', e => {
        const target = e.target;
        if (!target || !target.closest) return;
        if (!target.closest(`[id="variant-selects-${this.sectionId}"]`)) return;
        // Read the picker immediately, then once more after the theme's async
        // section render settles, in case it repaints the controls.
        requestAnimationFrame(() => this._syncFromMainForm());
        setTimeout(() => this._syncFromMainForm(), 700);
      });
    }

    /* ── Camouflage ─────────────────────────────────────────────────────── */

    // The Camouflage app hides variants per market on the main picker, but the
    // sticky bar renders its own option buttons from the full variant list. When
    // the app finishes a run it fires `hoos:executed` with its result, so the bar
    // hides the same option values and treats those variants as unavailable.
    // Fires for both the product-template flow and the featured-product flow
    // used on page templates. An option value is hidden only when every variant
    // carrying it is hidden.
    _bindCamouflage() {
      document.addEventListener('hoos:executed', e => {
        const hoos = e.detail;
        if (!hoos || !hoos.product) return;
        if (String(hoos.product.id) !== String(this.el.dataset.productId)) return;
        const hiddenIds = new Set((hoos.hiddenVariants || []).map(v => String(v.id)));
        this._applyCamouflage(hiddenIds);
      });
    }

    _applyCamouflage(hiddenIds) {
      this.variants.forEach(v => {
        v.camouflageHidden = hiddenIds.has(String(v.id));
      });

      this.optionBtns.forEach(btn => {
        const index = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.value;
        const anyVisible = this.variants.some(v => v.options[index] === value && !v.camouflageHidden);
        btn.toggleAttribute('hidden', !anyVisible);
        btn.style.display = anyVisible ? '' : 'none';
      });

      // A selection the app has now hidden is dropped back to the prompt, unless
      // gating is off, where the first visible variant takes its place.
      const current = this._findVariant();
      if (current && current.camouflageHidden) {
        if (this.el.dataset.requireVariant === 'false') {
          const next = this.variants.find(v => v.available && !v.camouflageHidden) || null;
          this._applyVariant(next);
        } else {
          this.selectedOptions = {};
          this.optionBtns.forEach(btn => btn.classList.remove('is-selected'));
          this.el.querySelectorAll('.sticky-atc__option-value').forEach(el => { el.textContent = ''; });
          this._applyVariant(null);
        }
      }
    }

    _setQuantity(value) {
      const next = Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
      this.quantity = next;
      this.qtyInputs.forEach(input => {
        if (input.value !== String(next)) input.value = next;
      });
      // The hidden field only matters if the form is ever submitted without JS.
      if (this.qtyHidden) this.qtyHidden.value = next;
    }

    _observeCartDrawer() {
      // Hide behind the cart drawer / any modal that locks the page. Reopening on
      // unlock has to re-check the main Add to cart: this theme also toggles
      // overflow-hidden-mobile / overflow-hidden-<breakpoint> for the menu drawers,
      // and a bare _show() here would pop the bar up at the top of the page.
      new MutationObserver(() => {
        if (document.body.classList.contains('overflow-hidden')) {
          this._hide();
        } else if (this._pastATC) {
          this._show();
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    _lowerChatWidget() {
      const apply = () => {
        const chat = document.getElementById('shopify-chat');
        if (chat) chat.style.zIndex = '9998';
      };
      apply();
      new MutationObserver(apply).observe(document.body, { childList: true, subtree: false });
    }

    _showSelectPrompt() {
      if (!this.formWrap || this.formWrap.querySelector('.sticky-atc__select-prompt')) return;
      const prompt = document.createElement('div');
      prompt.className = 'sticky-atc__select-prompt';
      prompt.textContent = 'Select options';
      this.formWrap.appendChild(prompt);
    }

    _hideSelectPrompt() {
      this.el.querySelectorAll('.sticky-atc__select-prompt').forEach(p => p.remove());
    }

    _observeMainATC() {
      this._atcButtons = Array.from(this._mainSubmitButtons());
      if (!this._atcButtons.length) return;

      const observer = new IntersectionObserver(() => this._evaluateATC(), { threshold: 0 });
      this._atcButtons.forEach(btn => observer.observe(btn));
      this._evaluateATC();
    }

    // The bar stands in for the main Add to cart once it is behind you, so it must
    // appear only after the shopper has scrolled PAST that button — never before
    // they have reached it.
    //
    // isIntersecting alone cannot tell those two apart: both read as "not
    // intersecting". On mobile the buy button starts well below the fold, so the
    // bar appeared at the top of the page, disappeared as the real button scrolled
    // through the viewport, then came back below it. Comparing rectangles supplies
    // the direction the observer leaves out.
    _evaluateATC() {
      const rects = this._atcButtons
        .map(btn => btn.getBoundingClientRect())
        // Both buy-button blocks can sit in the DOM with one hidden for the current
        // breakpoint; a hidden one has a zero-size rect and no opinion to offer.
        .filter(r => r.width > 0 || r.height > 0);

      if (!rects.length) {
        this._pastATC = false;
        this._hide();
        return;
      }

      const onScreen = rects.some(r => r.bottom > 0 && r.top < window.innerHeight);
      const past = !onScreen && rects.every(r => r.bottom <= 0);

      if (past !== this._pastATC) {
        this._pastATC = past;
        if (past) this._show();
        else this._hide();
      } else if (past) {
        // Already past it — keep the scroll-depth threshold live, and re-show if
        // something (a closed drawer whose class change we missed) left us hidden.
        if (!this._wantsVisible && !document.body.classList.contains('overflow-hidden')) {
          this._show();
        } else {
          this._applyThreshold();
        }
      }
    }

    // The option values currently chosen on the main picker, resolved against the
    // variant list. Read straight from the controls rather than from the form's
    // hidden variant input, because that input is only updated once the theme's
    // async section render lands — reading it on the change event gets the
    // PREVIOUS variant, which is how the bar ended up showing a stale price.
    _variantFromMainPicker() {
      const picker = document.querySelector(`[id="variant-selects-${this.sectionId}"]`);
      if (!picker) return null;

      const values = [];
      picker.querySelectorAll('select').forEach(sel => values.push(sel.value));
      if (!values.length) {
        // Swatch / pill pickers render radios per option group instead.
        picker.querySelectorAll('fieldset').forEach(group => {
          const checked = group.querySelector('input[type="radio"]:checked');
          values.push(checked ? checked.value : '');
        });
      }
      // A "Choose an option" placeholder is an empty value — nothing settled yet.
      if (!values.length || values.some(v => !v)) return null;

      return this.variants.find(v =>
        v.options.every((opt, i) => String(values[i]).trim() === String(opt).trim())
      ) || null;
    }

    _syncFromMainForm() {
      let variant = this._variantFromMainPicker();

      if (!variant) {
        let variantId = null;
        this._mainVariantInputs().forEach(input => {
          if (!variantId && input.value) variantId = parseInt(input.value);
        });
        if (!variantId) return;
        variant = this.variants.find(v => v.id === variantId);
      }

      if (variant) this._applyVariant(variant);
    }

    _getScrollThresholdPx() {
      const w = window.innerWidth;
      const vh = w < 750 ? this._thresholdMobile
               : w < 990 ? this._thresholdTablet
               : this._thresholdDesktop;
      return (vh / 100) * window.innerHeight;
    }

    _bindScrollThreshold() {
      // Re-evaluate against the buy button's position on scroll, not just the
      // threshold: it is the position that decides whether the bar belongs on
      // screen at all. Throttled to one read per frame.
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this._evaluateATC();
        });
      }, { passive: true });
    }

    _applyThreshold() {
      if (!this._wantsVisible) return;
      const visible = window.scrollY >= this._getScrollThresholdPx();
      this.el.classList.toggle('is-visible', visible);
      this.el.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    _show() {
      this._wantsVisible = true;
      this._applyThreshold();
    }

    _hide() {
      this._wantsVisible = false;
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
      this.collapsePanel();
    }

    togglePanel() {
      this.expanded ? this.collapsePanel() : this.expandPanel();
    }

    expandPanel() {
      this.expanded = true;
      this.el.classList.add('is-expanded');
      this.panel.removeAttribute('hidden');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'true');
    }

    collapsePanel() {
      this.expanded = false;
      this.el.classList.remove('is-expanded');
      this.panel.setAttribute('hidden', '');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'false');
    }

    _selectOption(btn) {
      if (btn.hasAttribute('data-unavailable')) return;

      const index = parseInt(btn.dataset.optionIndex);
      this.selectedOptions[index] = btn.dataset.value;

      // Update button states for this option group
      this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(b => {
        b.classList.toggle('is-selected', b === btn);
      });

      // Update displayed value
      const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
      if (display) display.textContent = btn.dataset.value;

      // Find and apply the matching variant
      const variant = this._findVariant();
      this._applyVariant(variant);

      // Also update the main product form so everything stays in sync
      this._syncToMainForm(variant);
    }

    // Shopify's canonical money formatter, over the shop's own format string —
    // the same one snippets/price.liquid renders with, handed over in
    // data-money-format so the repainted price matches the server's to the digit.
    _formatMoney(cents) {
      const fmt = this.moneyFormat;
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = fmt.match(placeholder);
      if (!match) return fmt;

      const num = (precision, thousands, decimal) => {
        if (cents == null || isNaN(cents)) return '0';
        const parts = (cents / 100).toFixed(precision).split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };

      let value;
      switch (match[1]) {
        case 'amount_no_decimals': value = num(0, ',', '.'); break;
        case 'amount_with_comma_separator': value = num(2, '.', ','); break;
        case 'amount_with_space_separator': value = num(2, ' ', ','); break;
        case 'amount_with_period_and_space_separator': value = num(2, ' ', '.'); break;
        case 'amount_no_decimals_with_comma_separator': value = num(0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator': value = num(0, ' ', ''); break;
        case 'amount_with_apostrophe_separator': value = num(2, "'", '.'); break;
        default: value = num(2, ',', '.');
      }
      return fmt.replace(placeholder, value);
    }

    // Repaint both copies of the price (bar and panel) from the variant itself.
    //
    // This is why it cannot just mirror the buy box: _syncToMainForm deliberately
    // does NOT dispatch a change on the main variant picker — that would trigger
    // the theme's async section fetch, which rewrites the URL and jumps the page
    // to the top. So the main price never re-renders for a sticky-driven choice,
    // and mirroring it would leave the bar showing the first variant's price
    // while the cart charged for the chosen one.
    //
    // Only the price-item spans are rewritten, so the price snippet's structure and
    // its visually-hidden labels survive.
    //
    // Written as innerHTML, not textContent: a money format may legitimately carry
    // markup — this shop's is `<span class='money'>${{amount}} NZD</span>`, the
    // wrapper currency apps key off — and as text that renders the tags literally
    // on screen. This is the same string the server puts inside the span via the
    // money filter, so the result matches a server render exactly.
    _paintPrice(variant) {
      if (!variant) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const price = this._formatMoney(variant.price);
      const compare = onSale ? this._formatMoney(variant.compare_at_price) : '';

      const setMoney = (node, html) => {
        if (node && node.innerHTML !== html) node.innerHTML = html;
      };

      this.el.querySelectorAll('.sticky-atc__price .price').forEach(p => {
        setMoney(p.querySelector('.price__regular .price-item--regular'), price);
        setMoney(p.querySelector('.price__sale .price-item--sale'), price);
        setMoney(p.querySelector('.price__sale s.price-item--regular'), compare);
        p.classList.toggle('price--on-sale', !!onSale);
        p.classList.toggle('price--sold-out', !variant.available);
      });

      this._ownPrice = true;
    }

    // Fallback for the state before any variant is settled (variant gating on,
    // nothing chosen yet): show whatever the buy box is showing. Once a variant
    // is applied, _paintPrice owns the price and this stands down.
    _getMainPriceEl() {
      const direct = document.getElementById(`price-${this.sectionId}`);
      if (direct) {
        const p = direct.querySelector('.price');
        if (p) return p;
      }
      // sm-reusable-product renders the price twice, as price-<id>-a / price-<id>-b,
      // one per layout variant. Prefer whichever is actually laid out.
      const host = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!host) return null;
      const candidates = host.querySelectorAll(`[id^="price-${this.sectionId}"] .price`);
      for (const p of candidates) {
        if (p.offsetParent !== null) return p;
      }
      return candidates[0] || null;
    }

    _mirrorPrice() {
      if (this._ownPrice) return;
      const mainPrice = this._getMainPriceEl();
      if (!mainPrice) return;
      const html = mainPrice.innerHTML;
      // Sync only the meaningful STATE classes — copying className wholesale would
      // drag the buy box's layout classes (price--large, price--show-badge) in.
      const onSale = mainPrice.classList.contains('price--on-sale');
      const soldOut = mainPrice.classList.contains('price--sold-out');
      this.el.querySelectorAll('.sticky-atc__price .price').forEach(p => {
        if (p.innerHTML !== html) p.innerHTML = html;
        p.classList.toggle('price--on-sale', onSale);
        p.classList.toggle('price--sold-out', soldOut);
      });
    }

    _observeMainPrice() {
      // Observe a stable ancestor: the price element itself is replaced wholesale
      // on variant change, which would detach an observer bound directly to it.
      const host = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!host) return;
      this._mirrorPrice();
      let t;
      new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => this._mirrorPrice(), 60);
      }).observe(host, { childList: true, subtree: true, characterData: true });
    }

    _findVariant() {
      return this.variants.find(v =>
        v.options.every((opt, i) => this.selectedOptions[i] === opt)
      ) || null;
    }

    _applyVariant(variant) {
      // Update selected options display to match variant
      if (variant) {
        variant.options.forEach((value, index) => {
          this.selectedOptions[index] = value;
          this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(btn => {
            btn.classList.toggle('is-selected', btn.dataset.value === value);
          });
          const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
          if (display) display.textContent = value;
        });
      }

      const available = variant && variant.available && !variant.camouflageHidden;

      if (this.variantInput && variant) {
        this.variantInput.value = variant.id;
      }

      this._paintPrice(variant);

      // Show or hide the selection prompt
      if (variant) {
        this.hasVariantSelected = true;
        this._hideSelectPrompt();
      } else {
        this.hasVariantSelected = false;
        this._showSelectPrompt();
      }

      // Update panel submit button
      if (this.panelSubmitBtn) {
        this.panelSubmitBtn.disabled = !available;
        const span = this.panelSubmitBtn.querySelector('span') || this.panelSubmitBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }

      // Update bar add button
      if (this.barAddBtn) {
        this.barAddBtn.disabled = !available;
        const span = this.barAddBtn.querySelector('span') || this.barAddBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }
    }

    _syncToMainForm(variant) {
      if (!variant) return;

      // Update the hidden variant ID input only — do not dispatch change events on
      // the variant selects, as that triggers the theme's async fetch which updates
      // the URL and causes the page to scroll to the top. Re-enable the input
      // first: "no variant selected by default" ships it disabled, and disabled
      // inputs are excluded from the submitted FormData.
      this._mainVariantInputs().forEach(input => {
        input.disabled = false;
        input.value = variant.id;
      });
    }

    _submitCart() {
      const variantId = this.variantInput ? parseInt(this.variantInput.value) : null;
      if (!variantId) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const btns = [this.panelSubmitBtn, this.barAddBtn].filter(Boolean);
      btns.forEach(btn => {
        btn.disabled = true;
        const span = btn.querySelector('span') || btn;
        span.textContent = 'Adding…';
      });

      const body = { items: [{ id: variantId, quantity: this.quantity }] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map(s => s.id).join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(parsedState => {
          btns.forEach(btn => {
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Added!';
          });

          if (cart && parsedState.sections) {
            // The cart element keeps `is-empty` from page load when the cart started
            // empty, and CSS uses it to hide the drawer contents — so without this the
            // drawer opens blank even though the line items rendered fine. Dawn's own
            // product-form does the same before renderContents.
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            const inner = cart.querySelector('.drawer__inner');
            if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
            cart.renderContents(parsedState);
          }

          this._hide();

          setTimeout(() => {
            const variant = this.variants.find(v => v.id === variantId);
            this._applyVariant(variant);
          }, 1800);
        })
        .catch(() => {
          btns.forEach(btn => {
            btn.disabled = false;
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Add to Cart';
          });
        });
    }
  }

  // The bar is re-parented onto <body>, so a theme-editor section reload leaves the
  // previous one behind while rendering a fresh copy. Drop the stale twin (same id)
  // before constructing, and flag each element so nothing is initialised twice.
  const init = root => {
    (root || document).querySelectorAll('.sticky-atc').forEach(el => {
      if (el.dataset.stickyAtcInit === 'true') return;
      el.dataset.stickyAtcInit = 'true';
      document.querySelectorAll(`[id="${el.id}"]`).forEach(other => {
        if (other !== el) other.remove();
      });
      new StickyATC(el);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
